import React, { useEffect, useState } from "react";
import api, { formatINR } from "../../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { XCircle, Trash2 } from "lucide-react";
import { toast, Toaster } from "sonner";

const STATUSES = ["placed", "confirmed", "packing", "packed", "shipped", "delivered", "cancelled"];

export default function AdminOrders() {
    const [items, setItems] = useState([]);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [confirmCancel, setConfirmCancel] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = () => api.get("/admin/orders").then(r => setItems(r.data));
    useEffect(() => { load(); }, []);

    const setStatus = async (id, status) => {
        try {
            await api.put(`/admin/orders/${id}/status`, null, { params: { status } });
            toast.success("Updated");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    const doCancel = async () => {
        if (!confirmCancel) return;
        setBusy(true);
        try {
            await api.put(`/admin/orders/${confirmCancel.id}/status`, null, { params: { status: "cancelled" } });
            toast.success(`Order #${confirmCancel.id.slice(0, 8)} cancelled`);
            setConfirmCancel(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        } finally {
            setBusy(false);
        }
    };

    const doDelete = async () => {
        if (!confirmDelete) return;
        setBusy(true);
        try {
            await api.delete(`/admin/orders/${confirmDelete.id}`);
            toast.success(`Order #${confirmDelete.id.slice(0, 8)} deleted`);
            setConfirmDelete(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="admin-orders">
            <Toaster position="top-center" />
            <div className="label-eyebrow text-sky-700">Fulfilment</div>
            <h1 className="font-serif text-4xl text-stone-900 mt-2 mb-2">Orders</h1>
            <p className="text-sm text-stone-600 mb-6">
                Update order status, cancel for soft write-off (preserves the row), or delete to hard-remove. All actions log to the admin activity log.
            </p>
            <div className="bg-white border border-stone-200">
                {items.map((o) => {
                    const cancelled = o.status === "cancelled";
                    return (
                        <div key={o.id} className={`border-b border-stone-100 last:border-b-0 p-4 ${cancelled ? "bg-stone-50" : ""}`} data-testid={`order-row-${o.id}`}>
                            <div className="grid grid-cols-12 gap-3 items-center">
                                <div className="col-span-2 text-xs font-mono text-stone-500">#{o.id.slice(0, 8)}</div>
                                <div className="col-span-3">
                                    <div className="font-medium">{o.address?.name}</div>
                                    <div className="text-xs text-stone-500">{o.address?.city}, {o.address?.state} · {o.address?.phone}</div>
                                </div>
                                <div className="col-span-1 text-xs">{o.items.length} items</div>
                                <div className="col-span-2 font-medium">{formatINR(o.total)}</div>
                                <div className="col-span-2">
                                    <Select value={o.status} onValueChange={(v) => setStatus(o.id, v)}>
                                        <SelectTrigger className="rounded-none border-stone-300 text-xs uppercase tracking-widest" data-testid={`order-status-${o.id}`}><SelectValue /></SelectTrigger>
                                        <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                                <div className="col-span-2 text-right">
                                    <div className="inline-flex gap-1">
                                        {!cancelled && (
                                            <button
                                                onClick={() => setConfirmCancel(o)}
                                                className="text-amber-700 hover:text-white hover:bg-amber-700 border border-amber-300 hover:border-amber-700 px-2 py-1 transition-colors inline-flex items-center text-xs"
                                                title="Soft cancel (keeps audit trail)"
                                                data-testid={`order-cancel-${o.id}`}
                                            >
                                                <XCircle className="w-3.5 h-3.5 mr-1" />Cancel
                                            </button>
                                        )}
                                        <button
                                            onClick={() => setConfirmDelete(o)}
                                            className="text-red-700 hover:text-white hover:bg-red-700 border border-red-300 hover:border-red-700 px-2 py-1 transition-colors inline-flex items-center text-xs"
                                            title="Hard delete (removes the row)"
                                            data-testid={`order-delete-${o.id}`}
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
                {items.length === 0 && <div className="p-8 text-center text-sm text-stone-500">No orders yet.</div>}
            </div>

            <AlertDialog open={!!confirmCancel} onOpenChange={(o) => !o && setConfirmCancel(null)}>
                <AlertDialogContent className="rounded-none">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Cancel order #{confirmCancel?.id.slice(0, 8)}?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This is a <strong>soft cancel</strong> — the row stays in the books with status "cancelled" so the audit trail is preserved. Use this for production write-offs.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="cancel-cancel">Keep order</AlertDialogCancel>
                        <AlertDialogAction onClick={doCancel} disabled={busy} className="bg-amber-600 hover:bg-amber-700 rounded-none" data-testid="cancel-confirm">
                            Cancel order
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
                <AlertDialogContent className="rounded-none">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Permanently delete order?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Order <span className="font-mono">#{confirmDelete?.id.slice(0, 8)}</span> for <strong>{confirmDelete?.address?.name}</strong> (<span className="font-mono">{formatINR(confirmDelete?.total)}</span>) will be removed entirely.
                            <span className="block mt-2 text-amber-700 text-xs">
                                Tip — for production books, prefer <strong>Cancel</strong> instead of Delete.
                            </span>
                            <span className="block mt-2 text-xs">This action is logged for admins.</span>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="delete-cancel">Keep</AlertDialogCancel>
                        <AlertDialogAction onClick={doDelete} disabled={busy} className="bg-red-600 hover:bg-red-700 rounded-none" data-testid="delete-confirm">
                            Delete forever
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
